import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Layout } from '@/components/layout'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import { useDocumentStore } from '@/lib/stores/documentStore'
import { FileBrowser } from '@/features/documents/components/FileBrowser'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { BookOpen, RefreshCw, AlertCircle, Clock } from 'lucide-react'
import { useHasPermission } from '@/lib/hooks/usePermission'

export function PlanvormingPage() {
    const { t } = useTranslation()
    const { activeCrisis } = useCrisisStore()
    const { getIndexStatus, triggerIndexing, error, clearCache, fetchAllDocuments, indexStatus } =
        useDocumentStore()
    const [isReindexing, setIsReindexing] = useState(false)
    const [reindexSuccess, setReindexSuccess] = useState(false)
    const [statusCheckInterval, setStatusCheckInterval] = useState<NodeJS.Timeout | null>(null)

    const canRefetchDocuments = useHasPermission('documents.refetch')

    // Fetch all documents on crisis activation for instant client-side search
    useEffect(() => {
        if (activeCrisis) {
            fetchAllDocuments(String(activeCrisis.id))
            getIndexStatus(String(activeCrisis.id))
        }
    }, [activeCrisis, fetchAllDocuments, getIndexStatus])

    // Poll indexing status while indexing is in progress
    useEffect(() => {
        if (!activeCrisis || indexStatus?.hasIndex) {
            // Cancel interval if indexing is complete or no crisis
            if (statusCheckInterval) {
                clearInterval(statusCheckInterval)
                setStatusCheckInterval(null)
            }
            return
        }

        // Start polling if indexing is not complete
        if (!statusCheckInterval) {
            const interval = setInterval(() => {
                getIndexStatus(String(activeCrisis.id))
            }, 5000) // Check every 5 seconds
            setStatusCheckInterval(interval)
        }

        return () => {
            if (statusCheckInterval) {
                clearInterval(statusCheckInterval)
                setStatusCheckInterval(null)
            }
        }
    }, [activeCrisis, indexStatus?.hasIndex, getIndexStatus, statusCheckInterval])

    const handleReindex = async () => {
        if (!activeCrisis || !canRefetchDocuments) return
        setIsReindexing(true)
        setReindexSuccess(false)
        try {
            await triggerIndexing(String(activeCrisis.id))
            clearCache()
            setReindexSuccess(true)
            setTimeout(() => setReindexSuccess(false), 5000)
        } catch {
            // error is set in the store
        } finally {
            setIsReindexing(false)
        }
    }

    return (
        <Layout>
            <div className="flex flex-col gap-6 p-6">
                {/* Header */}
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-orange-100 rounded-lg">
                            <BookOpen className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold tracking-tight">{t('planvorming.titel')}</h1>
                            <p className="text-muted-foreground text-sm mt-0.5">{t('planvorming.subtitel')}</p>
                        </div>
                    </div>
                    {canRefetchDocuments && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleReindex}
                            disabled={isReindexing || !activeCrisis}
                            className="flex items-center gap-2 shrink-0"
                        >
                            <RefreshCw className={`w-4 h-4 ${isReindexing ? 'animate-spin' : ''}`} />
                            {isReindexing ? t('documents.indexing') : t('documents.triggerIndexing')}
                        </Button>
                    )}
                </div>


                {/* Indexing in progress alert */}
                {indexStatus && !indexStatus.hasIndex && (
                    <Alert className="border-blue-200 bg-blue-50 flex items-start gap-3">
                        <Clock className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                            <AlertDescription className="text-blue-900 font-medium">
                                {t('documents.indexingInProgress') ||
                                    'SharePoint documents are being indexed...'}
                            </AlertDescription>
                            <p className="text-sm text-blue-800 mt-1">
                                {t('documents.indexingCheckBack') ||
                                    'This may take a few moments. Please check back in 2 minutes.'}
                            </p>
                        </div>
                    </Alert>
                )}

                {/* Success/error alerts */}
                {reindexSuccess && (
                    <Alert className="border-green-200 bg-green-50">
                        <AlertDescription className="text-green-800">
                            {t('documents.indexingStarted')}
                        </AlertDescription>
                    </Alert>
                )}
                {error && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {/* File explorer with integrated search (disabled while indexing) */}
                <Card className={indexStatus && !indexStatus.hasIndex ? 'opacity-50 pointer-events-none' : ''}>
                    <CardContent className="pt-6">
                        <FileBrowser />
                    </CardContent>
                </Card>
            </div>
        </Layout>
    )
}
